import { useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import { JsonFormsStateProps } from "components/CRPComponents/JsonForm/JsonFormsComponent/types";
import { ApiCMSRequest } from "data/ApiCMS";
import { ApiFinancialRequest } from "data/ApiFinancial";
import { SchemaData } from "../hooks/types";
import { UPDATE_QUERY, UpdateGql } from "../queries/update";
import {
  GENERATE_EVENT_FINANCIAL_QUERY,
  GenerateEventFinancialGql,
} from "../queries/generateFinancial";

const formatTicketValue = (value?: number | string) =>
  Number(value || 0).toLocaleString("pt-BR", { currency: "BRL", style: "currency" });

const getErrorMessage = (error: any) =>
  error?.response?.data?.errors?.[0]?.message ||
  error?.response?.data?.message ||
  error?.message ||
  "Confira os dados e tente novamente.";

const requiredFields: Array<keyof SchemaData> = [
  "title",
  "slug",
  "descriptionContentHTML",
  "localization",
  "dateInit",
  "dateFinish",
  "idMediaFileDesktop" as keyof SchemaData,
  "idMediaFileMobile" as keyof SchemaData,
  "address",
];

type Props = {
  queryParam: number;
  state?: JsonFormsStateProps<SchemaData>;
  setState?: React.Dispatch<React.SetStateAction<JsonFormsStateProps<SchemaData>>>;
  data?: SchemaData;
  onGenerated?: (generated: GenerateEventFinancialGql["generateEventFinancial"] & { financialPriceSyncUntil?: string }) => void;
  onSyncFinished?: () => void;
};

export function GenerateFinancialButton({
  queryParam,
  state,
  setState,
  data: dataProp,
  onGenerated,
  onSyncFinished,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [syncUntil, setSyncUntil] = useState(dataProp?.financialPriceSyncUntil || state?.data?.financialPriceSyncUntil || "");
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const onSyncFinishedRef = useRef(onSyncFinished);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    title: string;
    content?: string;
    color?: "success" | "error";
  }>({ open: false, title: "" });

  const data = dataProp ?? state?.data;
  const missingRequired = useMemo(() => {
    if (!data) return true;

    const missingBase = requiredFields.some((field) => {
      const value = data[field];
      return value === undefined || value === null || value === "";
    });

    const missingValue = data.ticketPrice === undefined || data.ticketPrice === null;

    return missingBase || missingValue;
  }, [data]);

  const isSynchronizing = Boolean(syncUntil);
  const disabled = loading || missingRequired || isSynchronizing;

  useEffect(() => {
    onSyncFinishedRef.current = onSyncFinished;
  }, [onSyncFinished]);

  useEffect(() => {
    const persistedUntil = dataProp?.financialPriceSyncUntil || state?.data?.financialPriceSyncUntil || "";
    if (persistedUntil && persistedUntil !== syncUntil) setSyncUntil(persistedUntil);
  }, [dataProp?.financialPriceSyncUntil, state?.data?.financialPriceSyncUntil, syncUntil]);

  useEffect(() => {
    if (!syncUntil) return;
    let cancelled = false;
    let finalizing = false;

    const finishSync = async () => {
      if (finalizing) return;
      finalizing = true;

      try {
        const response = await ApiCMSRequest<{ finishEventFinancialPriceSync?: boolean }>({
          query: `mutation FinishEventFinancialPriceSync($eventId: Int!) { finishEventFinancialPriceSync(eventId: $eventId) }`,
          variables: { eventId: queryParam },
        });
        const finished = response.data?.data?.finishEventFinancialPriceSync === true;
        if (!finished || cancelled) {
          finalizing = false;
          return;
        }

        setSyncUntil("");
        setRemainingSeconds(0);
        setState?.((previous) => ({
          ...previous,
          data: {
            ...previous?.data,
            status: "ACTIVE" as any,
            financialPriceSyncUntil: undefined,
          },
        }));
        onSyncFinishedRef.current?.();
      } catch {
        finalizing = false;
      }
    };

    const update = () => {
      const deadline = new Date(syncUntil).getTime();
      const remaining = Number.isFinite(deadline)
        ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
        : 0;
      setRemainingSeconds(remaining);
      if (remaining === 0) void finishSync();
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [queryParam, setState, syncUntil]);

  const handleGenerate = async () => {
    if (!data || disabled) return;

    let eventPrepared = false;
    try {
      setLoading(true);

      const prepareResponse = await ApiCMSRequest<{ prepareEventFinancialPriceSync?: boolean }>({
        query: `mutation PrepareEventFinancialPriceSync($eventId: Int!) { prepareEventFinancialPriceSync(eventId: $eventId) }`,
        variables: { eventId: queryParam },
      });
      if (prepareResponse?.data?.errors?.length) {
        throw prepareResponse.data.errors[0];
      }
      if (prepareResponse.data?.data?.prepareEventFinancialPriceSync !== true) {
        throw new Error("O CMS não confirmou a preparação do evento para a sincronização financeira.");
      }
      eventPrepared = true;
      setState?.((previous) => ({
        ...previous,
        data: {
          ...previous?.data,
          status: "DRAFT" as any,
          financialPriceSyncUntil: undefined,
        },
      }));

      const financialResponse = await ApiFinancialRequest<GenerateEventFinancialGql>({
        query: GENERATE_EVENT_FINANCIAL_QUERY,
        variables: {
          data: {
            eventId: queryParam,
            title: data.title,
            slug: data.slug,
            description: data.descriptionContentHTML,
            keywords: data.title,
            imageUrl: data.imageUrl,
            value: Number(data.ticketPrice || 0),
            ticketLimit:
              data.ticketLimit === undefined || data.ticketLimit === null
                ? undefined
                : Number(data.ticketLimit),
            eCommerceProductId: data.eCommerceProductId || undefined,
            eCommerceControlSKUId: data.eCommerceControlSKUId || undefined,
            eCommerceSkuId: data.eCommerceSkuId || undefined,
          },
        },
      });

      if (financialResponse?.data?.errors?.length) {
        throw financialResponse.data.errors[0];
      }

      const generated = financialResponse.data.data?.generateEventFinancial;
      if (!generated?.eCommerceSkuId) {
        throw new Error("A VTEX não retornou o SKU gerado.");
      }

      const updateResponse = await ApiCMSRequest<UpdateGql>({
        query: UPDATE_QUERY,
        variables: {
          data: {
            id: queryParam,
            saleEnabled: true,
            registration: formatTicketValue(data.ticketPrice),
            registrationLink: null,
            ticketPrice: Number(data.ticketPrice || 0),
            ticketLimit:
              data.ticketLimit === undefined || data.ticketLimit === null
                ? null
                : Number(data.ticketLimit),
            ticketSalesEndAt: data.ticketSalesEndAt || null,
            eCommerceProductId: generated.eCommerceProductId,
            eCommerceControlSKUId: generated.eCommerceControlSKUId,
            eCommerceSkuId: generated.eCommerceSkuId,
          },
        },
      });
      if (updateResponse?.data?.errors?.length) {
        throw updateResponse.data.errors[0];
      }
      if (!updateResponse.data?.data?.updateEvent) {
        throw new Error("O CMS não confirmou o salvamento dos identificadores financeiros da VTEX.");
      }

      setState?.((prev) => ({
        ...prev,
        data: {
          ...prev?.data,
          saleEnabled: true,
          registration: formatTicketValue(data.ticketPrice),
          eCommerceProductId: generated.eCommerceProductId,
          eCommerceControlSKUId: generated.eCommerceControlSKUId,
          eCommerceSkuId: generated.eCommerceSkuId,
        },
      }));

      const syncResponse = await ApiCMSRequest<{ beginEventFinancialPriceSync?: string }>({
        query: `mutation BeginEventFinancialPriceSync($eventId: Int!) { beginEventFinancialPriceSync(eventId: $eventId) }`,
        variables: { eventId: queryParam },
      });
      if (syncResponse?.data?.errors?.length) {
        throw syncResponse.data.errors[0];
      }
      const financialPriceSyncUntil = syncResponse.data?.data?.beginEventFinancialPriceSync || undefined;
      if (!financialPriceSyncUntil) {
        throw new Error("O CMS não confirmou o prazo de sincronização financeira.");
      }

      if (financialPriceSyncUntil) setSyncUntil(financialPriceSyncUntil);
      onGenerated?.({ ...generated, financialPriceSyncUntil });
      const wasUpdate = !!data.eCommerceSkuId;

      setSnackbar({
        open: true,
        title: wasUpdate ? "Financeiro atualizado" : "Financeiro gerado",
        content: `SKU VTEX ${generated.eCommerceSkuId} pronto para checkout.`,
        color: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        title: "Não foi possível sincronizar",
        content: `${getErrorMessage(error)}${eventPrepared ? " O evento foi mantido em RASCUNHO; corrija o problema e tente novamente." : ""}`,
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {syncUntil && (
        <Alert severity="warning" variant="filled" sx={{ mb: 2, fontWeight: 700, width: "100%" }}>
          {remainingSeconds > 0
            ? `Sincronizando o novo preço com a VTEX — ${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")} restantes.`
            : "Finalizando a sincronização financeira..."}{" "}
          Durante esse período o evento fica em RASCUNHO, não aparece para os alunos e o status não pode ser alterado. Essa pausa evita compras com o preço antigo enquanto os caches da VTEX e do carrinho são atualizados.
        </Alert>
      )}
      <MDButton
        variant="outlined"
        color="success"
        onClick={handleGenerate}
        loading={loading}
        disabled={disabled}
      >
        {data?.eCommerceSkuId ? "Atualizar financeiro" : "Gerar financeiro"}
      </MDButton>
      <MDSnackbar
        open={snackbar.open}
        title={snackbar.title}
        content={snackbar.content}
        color={snackbar.color}
        icon={snackbar.color === "error" ? "priority_high" : "check"}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </>
  );
}
